
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'
import { useRoute, useRouter } from 'vue-router'

// UI
import TheButton from '@/common/components/UI/TheButton/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import Alert from '@/common/components/UI/Alert/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'

// module components
import FTLCreateAdditionalService from '@/modules/OrdersHistoryModule/components/FTLCreateAdditionalService/index.vue'

// realisations
import { orders } from '@/core/realisations/orders.realisation'
import { FTL } from '@/core/realisations/FTL.realisation'
import { carriers } from '@/core/realisations/carriers.realisation'
import { companies } from '@/core/realisations/companies.realisation'
import { ftl_bids } from '@/core/realisations/ftl_bids.realisation'

// types
import { FullFTLOrderT, StockDeliveryNoteT } from '@/core/types/orders.types'
import { AdditionalServicesFTLI } from '@/core/types/additional_services.types'
import { UpdateFTLOrderPayloadI } from '@/core/types/ftl.types'
import { CarrierT } from '@/core/types/carriers.types'
import { CompanyT } from '@/core/types/companies.types'

// validaiton
import useVuelidate from '@vuelidate/core'
import { decimal, requiredIf } from '@vuelidate/validators'

// composable
import useNotifications from '@/common/composable/useNotifications'
import useAccount from '@/common/composable/useAccount'
import { DataState } from '@/core/classes/DataState.class'

export default defineComponent({
    components: {
        TheButton,
        Loader,
        TheInput,
        TheSelect,
        'ftl-create-additional-service': FTLCreateAdditionalService,
        Alert,
        Cheap,
    },
    setup() {
        const notifications = useNotifications()
        const { havePermission, havePermissions } = useAccount()

        const state = reactive({
            is_loading: false,
            is_failed: false,
            ftl_order: null as FullFTLOrderT | null,
            is_creating: false,
        })

        const avr_state = reactive({
            is_loading: false,
            already_have_1c: false,
        })

        const additional_state = reactive({
            is_open: false,
            stock: null as StockDeliveryNoteT | null,
        })

        const delete_additional_service_state = reactive({
            is_loading: false,
        })

        const router = useRouter()
        const route = useRoute()
        const order_id = Number(route.params.order_id)

        const form_state = reactive({
            // with_nds: false,
            cost_price: null as number | null,
            dop_order_number: null as string | null,
            paid_price: null as number | null,
            auto_count: null as number | null,
            auto_duration: null as number | null,
            auto_duration_client: null as number | null,
            carrier_id: null as number | null,
            company_id: null as number | null,
            //
            additional_service: [] as AdditionalServicesFTLI[],
            stock_delivery_notes: [] as StockDeliveryNoteT[],
        })

        const isFtlTypeTwo = computed(() => {
            return Boolean(state.ftl_order && state.ftl_order.ftl_type === 2)
        })

        const form_state$ = useVuelidate(
            {
                cost_price: {
                    required: requiredIf(() =>
                        Boolean(state.ftl_order && state.ftl_order.is_need_car)
                    ),
                    decimal,
                },
                // paid_price: {
                //     required: requiredIf(() =>
                //         Boolean(state.ftl_order && state.ftl_order.is_need_car)
                //     ),
                //     decimal,
                // },
                auto_count: {
                    required: requiredIf(() =>
                        Boolean(
                            state.ftl_order && state.ftl_order.is_need_car && isFtlTypeTwo.value
                        )
                    ),
                    decimal,
                },
                auto_duration: {
                    required: requiredIf(() =>
                        Boolean(
                            state.ftl_order && state.ftl_order.is_need_car && isFtlTypeTwo.value
                        )
                    ),
                    decimal,
                },
                // auto_duration_client: {
                //     required: requiredIf(() =>
                //         Boolean(
                //             state.ftl_order && state.ftl_order.is_need_car && isFtlTypeTwo.value
                //         )
                //     ),
                //     decimal,
                // },
                carrier_id: {
                    required: requiredIf(() =>
                        Boolean(state.ftl_order && state.ftl_order.is_need_car)
                    ),
                },
            },
            form_state
        )

        const data_state = reactive({
            carriers: new DataState<CarrierT>({}),
            companies: new DataState<CompanyT>({}),
        })

        // carriers

        const loadCarriers = async (page: number, search?: string) => {
            data_state.carriers.pagination.current_page = page

            try {
                const params = {
                    page,
                    search,
                    getAllCarriers: 1,
                    limit: 9999,
                    statusId: 2,
                }

                data_state.carriers.is_loading = true
                const response = await carriers.getList(params)
                data_state.carriers.list = response.data

                data_state.carriers.pagination.total_pages = response.meta.last_page

                data_state.carriers.is_failed = false
                data_state.carriers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.carriers.failure.catchError(error)

                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_failed = true
                }
            } finally {
                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_loading = false
                }
            }
        }

        const loadMoreCarriers = async (page: number) => {
            if (page > data_state.carriers.pagination.total_pages) return
            data_state.carriers.pagination.current_page = page

            try {
                const params = {
                    page,
                    getAllCarriers: 1,
                    limit: 9999,
                    statusId: 2,
                }

                data_state.carriers.is_loading = true

                const response = await carriers.getList(params)
                data_state.carriers.list = data_state.carriers.list.concat(response.data)

                data_state.carriers.pagination.total_pages = response.meta.last_page

                data_state.carriers.is_failed = false
                data_state.carriers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.carriers.failure.catchError(error)

                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_failed = true
                }
            } finally {
                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_loading = false
                }
            }
        }

        const searchCarriers = (search_query: string) => {
            loadCarriers(1, search_query)
        }

        const unshiftFindedCarrier = async (carrier: CarrierT) => {
            await loadCarriers(data_state.carriers.pagination.current_page)
            data_state.carriers.list.unshift(carrier)
        }

        // companies

        const loadCompanies = async (page: number, search?: string) => {
            data_state.companies.pagination.current_page = page

            try {
                const params = {
                    page,
                    search,
                    isExtended: 0,
                    statusId: 2,
                }

                data_state.companies.is_loading = true
                const response = await companies.getList(params)
                data_state.companies.list = response.data

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const loadMoreCompanies = async (page: number) => {
            if (page > data_state.companies.pagination.total_pages) return
            data_state.companies.pagination.current_page = page

            try {
                const params = {
                    page,
                    isExtended: 0,
                    statusId: 2,
                }

                data_state.companies.is_loading = true

                const response = await companies.getList(params)
                data_state.companies.list = data_state.companies.list.concat(response.data)

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const searchCompanies = (search_query: string) => {
            loadCarriers(1, search_query)
        }

        const unshiftFindedCompany = async (company: CompanyT) => {
            await loadCarriers(data_state.companies.pagination.current_page)
            data_state.companies.list.unshift(company)
        }

        //

        const loadFTLOrder = async () => {
            try {
                state.is_loading = true
                state.is_failed = false

                const response = await orders.getFTLOrder(order_id)
                state.ftl_order = response.data

                form_state.cost_price = response.data.cost_price
                form_state.dop_order_number = response.data.dop_order_number
                form_state.paid_price = response.data.paid_price
                form_state.auto_count = response.data.auto_count
                // form_state.with_nds = Boolean(response.data.cost_price_with_nds)
                form_state.auto_duration = response.data.auto_duration
                form_state.auto_duration_client = response.data.auto_duration_client
                form_state.additional_service = response.data.additional_service
                form_state.stock_delivery_notes = response.data.stock_delivery_notes
                form_state.carrier_id = response.data.carrier_id
                form_state.company_id = response.data.company_id
            } catch (error) {
                console.log(error)
                state.is_failed = true
            } finally {
                state.is_loading = false
            }
        }

        const checkAvr = async () => {
            try {
                avr_state.is_loading = true

                const response = await ftl_bids.checkAvr(order_id)
                avr_state.already_have_1c = response.data.isPosted
            } catch (error) {
                console.error(error)
            } finally {
                avr_state.is_loading = false
            }
        }

        onBeforeMount(() => {
            loadFTLOrder()
            loadCarriers(1)
            loadCompanies(1)
            checkAvr()
        })

        const saveOrder = async () => {
            form_state$.value.$touch()
            if (form_state$.value.$invalid) return

            if (avr_state.already_have_1c) {
                const answer = confirm(
                    'По данному ФТЛ заказу уже проведен документ в 1с. Вы уверены что хотите поменять значения ?'
                )
                if (!answer) return
            }

            try {
                state.is_creating = true

                const additional_services = [...form_state.additional_service]
                form_state.stock_delivery_notes.forEach((s) => {
                    s.additional_service.forEach((item) => additional_services.push(item))
                })

                const payload: UpdateFTLOrderPayloadI = {
                    cost_price: form_state.cost_price as number,
                    dop_order_number: form_state.dop_order_number,
                    paid_price: form_state.paid_price as number,
                    auto_count: form_state.auto_count as number,
                    // with_nds: Number(form_state.with_nds),
                    auto_duration: form_state.auto_duration as number,
                    auto_duration_client: Number(form_state.auto_duration_client),
                    carrier_id: form_state.carrier_id as number | null,
                    company_id: form_state.company_id as number,
                    additional_services,
                }

                await FTL.updateOrder(order_id, payload)

                notifications.notify({
                    title: 'Заказ отредактирован',
                    type: 'success',
                })

                router.go(-1)
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'Ошибка сохранения FTL заказа',
                })
            } finally {
                state.is_creating = false
            }
        }

        const deleteAdditionalService = async (index: number, additional_service_id: number) => {
            if (delete_additional_service_state.is_loading) return

            const answer = confirm('Вы действительно хотите удалить доп.услугу?')
            if (!answer) return

            try {
                delete_additional_service_state.is_loading = true

                await FTL.deleteOrderAdditionalService(additional_service_id)

                notifications.notify({ title: 'Доп. услуга удалена', type: 'success' })
                loadFTLOrder()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При удалении доп. услуги произошла ошибка',
                })
            } finally {
                delete_additional_service_state.is_loading = false
            }
        }

        const toggleAdditionalService = (stock?: StockDeliveryNoteT) => {
            additional_state.is_open = !additional_state.is_open
            if (stock) {
                additional_state.stock = stock
            } else {
                additional_state.stock = null
            }
        }

        const onCarrierChange = (newWalue: number, additionalService: AdditionalServicesFTLI) => {
            const carrier = data_state.carriers.list.find((c) => c.id === newWalue)
            if (carrier) {
                additionalService.carrier_name = carrier.name
            }
        }

        const getCostTotal = (additionalService: AdditionalServicesFTLI) => {
            const value = Number(additionalService.value)
            const duration = Number(additionalService.duration)
            const cost_per_hour = Number(additionalService.cost_per_hour)

            return value * duration * cost_per_hour
        }

        const getPaidPriceTotal = (additionalService: AdditionalServicesFTLI) => {
            const value = Number(additionalService.value)
            const duration_client = Number(additionalService.duration_client)
            const paid_price_per_hour = Number(additionalService.paid_price_per_hour)

            return value * duration_client * paid_price_per_hour
        }

        const onSubmitServices = () => {
            toggleAdditionalService()
            loadFTLOrder()
        }

        const getNdsText = (isNds: boolean) => {
            return isNds ? 'НДС включен' : 'НДС не включен'
        }

        const getNdsClass = (isNds: boolean) => {
            let cheapClass = 'small ml-3 '

            if (isNds) cheapClass += 'green'

            return cheapClass
        }

        const isCanAddService = computed(() =>
            havePermission('cabinet-ftl-order-additional-service-create')
        )
        const isCarrierDisabled = computed(() => {
            return Boolean(
                state.ftl_order &&
                    state.ftl_order.status_code === 'on_the_way' &&
                    isFtlTypeTwo.value
            )
        })

        return {
            avr_state,
            state,
            data_state,
            additional_state,
            delete_additional_service_state,
            //
            loadFTLOrder,
            form_state,
            form_state$,
            saveOrder,
            deleteAdditionalService,
            havePermissions,
            getCostTotal,
            getPaidPriceTotal,
            getNdsText,
            getNdsClass,
            //
            loadCarriers,
            loadMoreCarriers,
            searchCarriers,
            unshiftFindedCarrier,
            onCarrierChange,
            //
            loadCompanies,
            loadMoreCompanies,
            searchCompanies,
            unshiftFindedCompany,
            //
            toggleAdditionalService,
            onSubmitServices,
            //
            isCanAddService,
            isFtlTypeTwo,
            isCarrierDisabled,
        }
    },
})
